import { useEffect } from 'react'
import { Handle, Position, useNodeConnections, useUpdateNodeInternals } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { fnArgLiteral } from '../../lib/functionArgs.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { selectedBorder } from './selectedBorder.js'
import { execInHandle, execOutHandle, dataInHandle, dataOutHandle } from '../../lib/macroGraph.js'

/**
 * Event-graph MACRO nodes (macroGraph.js has the model and the runtime rules).
 *
 *  - MacroNode: the instance on the outer canvas — one pin per declared input
 *    (left) and output (right), exec pins included. Double-click opens the body.
 *  - MacroInputNode / MacroOutputNode: the two locked tunnels inside the body.
 *    The editor injects the signature as `data.macroSig`.
 *
 * Handle ids are keyed by the pin's stable id, so the pin list can change under
 * a node — which is why each component tells React Flow to re-measure its
 * handles whenever the signature does.
 */

export const MACRO_ACCENT = '#e0a040'
const BORDER_IDLE = '#4a3410'
const EXEC_STYLE = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
export const MACRO_PIN_COLOR = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8', any: '#9a9a9a', exec: '#dddddd' }

const pinStyle = (type) => type === 'exec' ? EXEC_STYLE : ({
  background: MACRO_PIN_COLOR[type] ?? '#888',
  width: 10, height: 10, borderRadius: '50%',
  border: `2px solid ${(MACRO_PIN_COLOR[type] ?? '#888') + '66'}`,
})

const stop = e => e.stopPropagation()
const argInputStyle = {
  width: 64, boxSizing: 'border-box',
  background: '#140c02', border: '1px solid #e0a04055', color: '#f5e2c0',
  borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', padding: '2px 5px', outline: 'none',
}

// Re-measure handles when the signature changes, or wires attach to stale positions.
function useSignatureInternals(id, inputs, outputs) {
  const update = useUpdateNodeInternals()
  const key = [...inputs, ...outputs].map(p => `${p.id}:${p.type}`).join(',')
  useEffect(() => { update(id) }, [id, key, update])
}

// An unwired data input carries an inline literal (`data.args[pin.id]`), like a
// Function Call's parameters. `any` pins have no type to edit a literal in.
function MacroArg({ nodeId, pin, data }) {
  const wired = useNodeConnections({ id: nodeId, handleType: 'target', handleId: dataInHandle(pin.id) }).length > 0
  if (wired || pin.type === 'any') return null
  const raw = data.args?.[pin.id]
  const value = fnArgLiteral(pin.type, raw)
  const setArg = v => data.onChange?.('args', { ...(data.args ?? {}), [pin.id]: v })
  const common = {
    'aria-label': `macro argument ${pin.name}`,
    onPointerDown: stop, onMouseDown: stop, onClick: stop, onKeyDown: stop, onWheel: stop,
  }
  if (pin.type === 'bool') {
    return <input className={nodeControlClass()} type="checkbox" checked={value} onChange={e => setArg(e.target.checked)} {...common} style={{ accentColor: MACRO_PIN_COLOR.bool }} />
  }
  if (pin.type === 'int' || pin.type === 'float') {
    return <input className={nodeControlClass()} type="number" step={pin.type === 'int' ? 1 : 0.1}
      value={typeof raw === 'string' || typeof raw === 'number' ? raw : value}
      onChange={e => setArg(e.target.value)} {...common} style={argInputStyle} />
  }
  return <input className={nodeControlClass()} type="text" value={value} placeholder="value" onChange={e => setArg(e.target.value)} {...common} style={argInputStyle} />
}

function PinLabel({ pin, align }) {
  return (
    <span style={{ color: pin.type === 'exec' ? '#d8c8a8' : '#a08a60', fontSize: 9, textAlign: align }}>
      {pin.name}
      {pin.type !== 'exec' && <span style={{ color: '#5a4828', fontSize: 8 }}> ({pin.type})</span>}
    </span>
  )
}

// ── Macro instance ──────────────────────────────────────────────────────────
export function MacroNode({ id, data, selected }) {
  const inputs = data.inputs ?? []
  const outputs = data.outputs ?? []
  const count = (data.innerNodes ?? []).length - 2
  const fired = isFired(data)
  useSignatureInternals(id, inputs, outputs)
  const rows = Math.max(inputs.length, outputs.length)

  return (
    <div
      data-testid="macro-node"
      onDoubleClick={e => { e.stopPropagation(); data.onOpenGroup?.() }}
      style={{
        background: selected ? '#1c1304' : '#140d02',
        border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
        borderRadius: 6, minWidth: 190, fontFamily: 'var(--m-font)', cursor: 'default',
        boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
      }}
    >
      <div style={{
        background: selected ? '#33240a' : '#271b06', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, borderRadius: '4px 4px 0 0',
      }}>
        <span style={{ color: MACRO_ACCENT, fontSize: 12 }}>⧉</span>
        <span style={{ color: MACRO_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5, flex: 1 }}>
          {data.name || 'Macro'}
        </span>
      </div>

      <div style={{ padding: '4px 0' }}>
        {Array.from({ length: rows }, (_, i) => {
          const a = inputs[i], b = outputs[i]
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', minHeight: 20, position: 'relative' }}>
              <div className="nodrag nowheel" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 5, padding: '2px 8px 2px 16px', position: 'relative' }}>
                {a && <>
                  <Handle type="target" position={Position.Left} id={a.type === 'exec' ? execInHandle(a.id) : dataInHandle(a.id)} style={pinStyle(a.type)} />
                  <PinLabel pin={a} />
                  {a.type !== 'exec' && <MacroArg nodeId={id} pin={a} data={data} />}
                </>}
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5, padding: '2px 16px 2px 8px', position: 'relative' }}>
                {b && <>
                  <PinLabel pin={b} align="right" />
                  <Handle type="source" position={Position.Right} id={b.type === 'exec' ? execOutHandle(b.id) : dataOutHandle(b.id)} style={pinStyle(b.type)} />
                </>}
              </div>
            </div>
          )
        })}
      </div>

      <div style={{ padding: '2px 12px 5px', color: '#5a4320', fontSize: 9, fontStyle: 'italic' }}>
        {count > 0 ? `${count} node${count !== 1 ? 's' : ''} inside · ` : ''}double-click to open
      </div>
    </div>
  )
}

function TunnelShell({ title, selected, fired, children }) {
  return (
    <div style={{
      background: selected ? '#1c1304' : '#140d02',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 140, fontFamily: 'var(--m-font)', cursor: 'default',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{ background: '#271b06', padding: '6px 12px', borderRadius: '4px 4px 0 0' }}>
        <span style={{ color: MACRO_ACCENT, fontSize: 9, letterSpacing: 0.8, fontWeight: 'bold' }}>{title}</span>
      </div>
      <div style={{ padding: '4px 0' }}>{children}</div>
    </div>
  )
}

// ── Inputs tunnel: emits every input of the instance ────────────────────────
export function MacroInputNode({ id, data, selected }) {
  const inputs = data.macroSig?.inputs ?? []
  useSignatureInternals(id, inputs, [])
  return (
    <TunnelShell title="INPUTS" selected={selected} fired={isFired(data)}>
      {inputs.map(pin => (
        <div key={pin.id} style={{ padding: '3px 16px 3px 12px', display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
          <PinLabel pin={pin} align="right" />
          <Handle type="source" position={Position.Right} id={pin.type === 'exec' ? execOutHandle(pin.id) : dataOutHandle(pin.id)} style={pinStyle(pin.type)} />
        </div>
      ))}
      {inputs.length === 0 && <div style={{ padding: '2px 12px', color: '#5a4320', fontSize: 9, fontStyle: 'italic' }}>no inputs</div>}
    </TunnelShell>
  )
}

// ── Outputs tunnel: receives every output of the instance ───────────────────
export function MacroOutputNode({ id, data, selected }) {
  const outputs = data.macroSig?.outputs ?? []
  useSignatureInternals(id, [], outputs)
  return (
    <TunnelShell title="OUTPUTS" selected={selected} fired={isFired(data)}>
      {outputs.map(pin => (
        <div key={pin.id} style={{ padding: '3px 12px 3px 16px', display: 'flex', position: 'relative' }}>
          <Handle type="target" position={Position.Left} id={pin.type === 'exec' ? execInHandle(pin.id) : dataInHandle(pin.id)} style={pinStyle(pin.type)} />
          <PinLabel pin={pin} />
        </div>
      ))}
      {outputs.length === 0 && <div style={{ padding: '2px 12px', color: '#5a4320', fontSize: 9, fontStyle: 'italic' }}>no outputs</div>}
    </TunnelShell>
  )
}
